/**
 * The startup screen shown when a deployment's configuration cannot be used (TASK-0076).
 *
 * It is written in plain DOM on purpose. The application's own React tree, its providers, its query client and its API
 * client all assume a configured address, so the one screen that reports a *missing* address must not depend on them —
 * otherwise the failure it describes would itself crash.
 *
 * The copy is Persian and right-to-left, like the document (`index.html` ships `lang="fa-IR" dir="rtl"`), and it is
 * duplicated from the catalogue deliberately: `lib/i18n` is a lazily loaded bundle, and this screen is exactly the case
 * where the bundle may fail to arrive. The English sentence is given as a second line so an operator who does not read
 * Persian is not stuck, and the message names the **file**, the **key** and the **reason** — never a configured value.
 */

import { PROBLEM_MESSAGES } from '@/lib/configurationMessages';
import type { ConfigurationProblem } from '@/lib/runtimeConfig';

/** Persian heading of the screen. */
export const STARTUP_ERROR_TITLE = 'پیکربندی این نصب کامل نیست';

/** Persian instruction that follows the technical detail. */
export const STARTUP_ERROR_HINT =
  'فایل config.json را کنار index.html روی سرور اصلاح کنید و صفحه را دوباره بارگذاری کنید.';

/**
 * Builds the English explanation of one problem.
 *
 * @param problem The problem to explain.
 * @param configUrl The URL the runtime file was read from.
 * @returns One sentence an operator can act on.
 */
export function describeProblem(problem: ConfigurationProblem, configUrl: string): string {
  const where = problem.key === 'config.json' ? configUrl : `"${problem.key}" in ${configUrl}`;

  return `${where}: ${PROBLEM_MESSAGES[problem.code]}.`;
}

/** One row of the screen. */
interface ErrorRow {
  /** Persian label. */
  label: string;
  /** Technical detail, in English, one line per problem. */
  detail: string;
}

/**
 * Renders the startup failure into the root container instead of the application.
 *
 * @param container The element the application would have rendered into.
 * @param problems The problems found while resolving the configuration.
 * @param configUrl The URL the runtime file was read from.
 */
export function renderStartupError(
  container: HTMLElement,
  problems: ConfigurationProblem[],
  configUrl: string,
): void {
  const rows: ErrorRow[] = problems.map((problem) => ({
    label: problem.key,
    detail: describeProblem(problem, configUrl),
  }));

  container.textContent = '';
  container.setAttribute('dir', 'rtl');
  container.setAttribute('lang', 'fa-IR');

  const panel = document.createElement('main');
  panel.setAttribute('role', 'alert');
  panel.setAttribute('aria-live', 'assertive');
  panel.className =
    'mx-auto flex min-h-screen max-w-2xl flex-col justify-center gap-4 p-6 font-sans text-foreground';

  const title = document.createElement('h1');
  title.className = 'text-xl font-semibold';
  title.textContent = STARTUP_ERROR_TITLE;

  const hint = document.createElement('p');
  hint.className = 'text-sm text-muted-foreground';
  hint.textContent = STARTUP_ERROR_HINT;

  const list = document.createElement('ul');
  list.className =
    'flex list-disc flex-col gap-2 rounded-md border border-destructive/40 bg-muted p-4 ps-8';

  for (const row of rows) {
    const item = document.createElement('li');
    item.className = 'text-sm';

    const key = document.createElement('code');
    key.className = 'font-mono text-xs';
    key.textContent = row.label;

    const detail = document.createElement('pre');
    detail.className =
      'mt-1 whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground';
    detail.dir = 'ltr';
    detail.lang = 'en';
    detail.textContent = row.detail;

    item.append(key, detail);
    list.append(item);
  }

  panel.append(title, hint, list);
  container.append(panel);
}
